import {
  CirclePlay,
  Gauge,
  Handshake,
  IndianRupee,
  MessagesSquare,
  Repeat,
  ShoppingCart,
  Sparkles,
  TicketPercent,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { featurePath } from '@/features/marketing/nav';
import { registerSiteContent } from '@/features/siteContent/runtime';

/**
 * The marketing header's menus and labels. Editable by the platform owner
 * under Site content -> Header; the icons stay with their slots.
 */

export const HEADER_LABELS = {
  whyMenu: 'Why NeonCarts',
  productsMenu: 'Products',
  pricing: 'Pricing',
  enterprise: 'Enterprise',
  resourcesMenu: 'Resources',
  builtInHeading: 'Built into every store',
  whyMoreLink: 'Why merchants choose NeonCarts',
  login: 'Log in',
  startTrial: 'Create your store',
};

export interface WhyHighlight {
  icon: LucideIcon;
  title: string;
  description: string;
  to: string;
}

export const WHY_HIGHLIGHTS: WhyHighlight[] = [
  {
    icon: Gauge,
    title: 'Launch fast',
    description: 'From signup to a live store in about 10 minutes - no code, no agency.',
    to: '/why-shoplaunch#why-day-one',
  },
  {
    icon: Repeat,
    title: 'Switch to NeonCarts',
    description: 'Bring your catalogue over and keep selling - we make moving painless.',
    to: '/why-shoplaunch#why-switching',
  },
  {
    icon: IndianRupee,
    title: 'Built for India',
    description: 'GST invoices, UPI and COD, WhatsApp Commerce - native, not bolted on.',
    to: '/why-shoplaunch#why-india',
  },
];

export const WHY_BUILT_IN: WhyHighlight[] = [
  {
    icon: ShoppingCart,
    title: 'Checkout that converts',
    description: 'Fast, mobile-first and honest.',
    to: featurePath('checkout'),
  },
  {
    icon: Sparkles,
    title: 'AI assistant',
    description: 'Your commerce co-pilot, included.',
    to: featurePath('ai-assistant'),
  },
];

/**
 * Everything under "Resources" that actually exists.
 *
 * Short on purpose. A blog and written guides would also belong here, but
 * neither is built, and a menu item that lands on the 404 page costs more
 * trust than the missing item does.
 */
export const RESOURCE_LINKS: WhyHighlight[] = [
  {
    icon: CirclePlay,
    title: 'Video help centre',
    description: 'Short, simple videos for every step - from setup to your first order.',
    to: '/help',
  },
  {
    icon: TicketPercent,
    title: "Today's Deals",
    description: 'Live offers for new merchants, applied when you sign up.',
    to: '/deals',
  },
  {
    icon: Handshake,
    title: 'Become a partner',
    description: 'Refer merchants and earn on every store you bring over.',
    to: '/become-a-partner',
  },
  {
    icon: MessagesSquare,
    title: 'Talk to sales',
    description: 'A large catalogue to migrate, or a plan that needs shaping.',
    to: '/talk-to-sales',
  },
];

registerSiteContent('header', { HEADER_LABELS, WHY_HIGHLIGHTS, WHY_BUILT_IN, RESOURCE_LINKS });
